// v1.5.0 · CRM comercial: comportamiento del consumidor y buyer persona (5 pantallas animadas)
import React from 'react';
import { useTick, Count, Head, Live, Kpi, Status, wave, useReduced, fmt } from './kit';

/* 1 · Buyer persona */
const TRAITS = [['Decide con datos', 86], ['Sensible al precio', 58], ['Necesita aprobación del directorio', 72], ['Prefiere proveedores locales', 64]];
const CHANNELS = [['LinkedIn', 44], ['WhatsApp', 31], ['Correo', 17], ['Eventos', 8]];
export function CrmPersona() {
  const t = useTick(2600);
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1fr_1.3fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[rgba(87,211,255,.15)] text-[16px] font-extrabold text-[#9FE6FF] ring-2 ring-[#57D3FF]">CG</div>
          <div><div className="text-[15px] font-bold">Carla, la gerente que compara</div><div className="dim text-[11.5px]">Perfil tipo, gerente de operaciones, 38 a 50 años</div></div>
        </div>
        <div className="dim mt-1 text-[11px] font-semibold">Qué la mueve</div>
        <p className="text-[12px] leading-snug">Reducir errores de su equipo y tener reportes listos para el directorio.</p>
        <div className="dim text-[11px] font-semibold">Qué la frena</div>
        <p className="text-[12px] leading-snug">Proyectos que se alargan y costos que cambian a mitad de camino.</p>
        <div className="mt-auto"><Status tone="info">Segmento con mayor valor de vida</Status></div>
      </div>
      <div className="flex min-h-0 flex-col gap-3">
        <div className="dash-panel p-3">
          <div className="dim mb-2 text-[11px] font-semibold">Rasgos de comportamiento</div>
          {TRAITS.map(([n, v], i) => (
            <div key={n} className="mb-2 text-[11.5px] last:mb-0">
              <div className="flex justify-between"><span>{n}</span><span className="num font-semibold">{Math.round(v + wave(t, i + 1, 3))} %</span></div>
              <div className="mt-1 h-1.5 rounded bg-white/10"><div className="bar-live-x h-full rounded bg-[#2E9BD6]" style={{ transform: `scaleX(${(v + wave(t, i + 1, 3)) / 100})` }} /></div>
            </div>
          ))}
        </div>
        <div className="dash-panel p-3">
          <div className="dim mb-2 text-[11px] font-semibold">Dónde llega el primer contacto</div>
          <div className="flex h-3 overflow-hidden rounded">
            {CHANNELS.map(([n, v], i) => <div key={n} className="grow-x h-full" style={{ width: `${v}%`, background: ['#2E9BD6', '#C7811F', '#8B6BE0', '#5E7E90'][i], marginRight: 2, '--d': `${i * 120}ms` }} title={`${n} ${v} %`} />)}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px]">
            {CHANNELS.map(([n, v], i) => <span key={n} className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm" style={{ background: ['#2E9BD6', '#C7811F', '#8B6BE0', '#5E7E90'][i] }} />{n} {v} %</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* 2 · Recorrido del cliente (embudo con flujo) */
const FUNNEL = [['Visita la web', 4200], ['Descarga el brochure', 940], ['Agenda reunión', 310], ['Recibe propuesta', 120], ['Firma contrato', 39]];
export function CrmRecorrido() {
  const t = useTick(1800);
  const reduced = useReduced();
  const max = FUNNEL[0][1];
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Recorrido del cliente" sub="Últimos 90 días" right={<Live />} />
      <div className="dash-panel relative min-h-0 flex-1 overflow-hidden p-3">
        <div className="flex h-full flex-col justify-around">
          {FUNNEL.map(([n, v], i) => {
            const val = v + (i === 0 ? t * 3 : 0);
            const conv = i ? Math.round((v / FUNNEL[i - 1][1]) * 100) : null;
            return (
              <div key={n} className="flex items-center gap-3">
                <span className="w-[130px] shrink-0 truncate text-[12px]">{n}</span>
                <div className="relative h-7 flex-1">
                  <div className="grow-x absolute inset-y-0 left-0 rounded-r-[4px] bg-[#2E9BD6]" style={{ width: `${Math.max(4, (Math.sqrt(v / max)) * 100)}%`, opacity: 1 - i * 0.12, '--d': `${i * 120}ms` }} />
                  {!reduced && i < 4 && [0, 1, 2].map((k) => (
                    <span key={k} className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[#A6ECFF]"
                          style={{ animation: `flow-dot 2.4s linear ${k * 0.8 + i * 0.3}s infinite`, left: 0 }} />
                  ))}
                </div>
                <span className="num w-14 shrink-0 text-right text-[12px] font-bold">{fmt(val)}</span>
                <span className="num dim hidden w-12 shrink-0 text-right text-[11px] sm:block">{conv !== null ? `${conv} %` : ''}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="dim text-[11.5px]">El mayor salto está entre reunión y propuesta: ahí el seguimiento automático marca la diferencia.</p>
    </div>
  );
}

/* 3 · Segmentos por comportamiento de compra */
const SEGS = [
  { n: 'Leales', c: '#2E9BD6', x: 330, y: 70, k: 9, note: 'Compran cada trimestre' },
  { n: 'Nuevos', c: '#C7811F', x: 120, y: 90, k: 7, note: 'Primera compra este mes' },
  { n: 'En riesgo', c: '#8B6BE0', x: 320, y: 210, k: 6, note: 'Sin compra en 120 días' },
  { n: 'Dormidos', c: '#5E7E90', x: 110, y: 220, k: 5, note: 'Más de un año inactivos' },
];
export function CrmSegmentos() {
  const t = useTick(1600);
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.5fr_1fr]">
      <div className="dash-panel min-h-0 overflow-hidden p-2">
        <svg viewBox="0 0 440 290" className="h-full w-full" role="img" aria-label="Clientes agrupados en 4 segmentos según frecuencia y recencia de compra">
          <text x="220" y="286" textAnchor="middle" fontSize="10" fill="#86A6B6">Frecuencia de compra →</text>
          <text x="10" y="145" textAnchor="middle" fontSize="10" fill="#86A6B6" transform="rotate(-90 10 145)">Compra reciente →</text>
          {SEGS.map((s, si) => (
            <g key={s.n}>
              {Array.from({ length: s.k * 2 }, (_, i) => {
                const a = i * 2.4 + si; const r = 14 + (i % 4) * 11;
                const x = s.x + Math.cos(a) * r + wave(t, i + si, 3); const y = s.y + Math.sin(a) * r * 0.7 + wave(t, i + 2 + si, 3);
                return <circle key={i} className="move" r="4.5" fill={s.c} stroke="#0C2434" strokeWidth="2" style={{ transform: `translate(${x}px, ${y}px)` }} />;
              })}
              <text x={s.x} y={s.y - 54} textAnchor="middle" fontSize="11.5" fontWeight="800" fill="#DDF0F7">{s.n}</text>
            </g>
          ))}
        </svg>
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Segmentos" sub="Recencia, frecuencia y monto" />
        {SEGS.map((s, i) => (
          <div key={s.n} className="text-[12px]">
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 font-semibold"><i className="h-2.5 w-2.5 rounded-full" style={{ background: s.c }} />{s.n}</span><span className="num font-bold">{[38, 27, 21, 14][i]} %</span></div>
            <div className="dim text-[11px]">{s.note}</div>
          </div>
        ))}
        <p className="mt-auto rounded-md bg-[rgba(139,107,224,.14)] p-2 text-[11.5px] leading-snug">Clientes en riesgo reciben una oferta personalizada antes de que se pierdan.</p>
      </div>
    </div>
  );
}

/* 4 · Mapa de calor de interacciones */
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
const heat = (d, h) => { const work = d < 5 ? 1 : 0.3; const peak = Math.exp(-((h - 10) ** 2) / 6) * 0.9 + Math.exp(-((h - 16) ** 2) / 5) * 0.6; return Math.min(1, work * peak + (d === 1 ? 0.12 : 0)); };
export function CrmCalor() {
  const t = useTick(700);
  const hot = [t % 7 < 5 ? t % 5 : 1, 1 + (t % 6)];
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Cuándo responden los clientes" sub="Apertura de correos y respuestas de WhatsApp por hora"
            right={<Status tone="ok">Mejor momento: martes 10:00</Status>} />
      <div className="dash-panel min-h-0 flex-1 overflow-x-auto p-3">
        <div className="grid min-w-[420px] gap-[3px]" style={{ gridTemplateColumns: `36px repeat(${HOURS.length}, 1fr)` }}>
          <span />
          {HOURS.map((h) => <span key={h} className="dim num text-center text-[10px]">{h}</span>)}
          {DAYS.map((d, di) => (
            <React.Fragment key={d}>
              <span className="dim text-[10.5px] leading-[26px]">{d}</span>
              {HOURS.map((h, hi) => {
                const v = heat(di, h); const ping = hot[0] === di && hot[1] === hi;
                return (
                  <span key={h} title={`${d} ${h}:00, ${Math.round(v * 100)} % de respuesta`}
                        className="relative h-[26px] rounded-[3px] transition-[background-color] duration-500"
                        style={{ background: `rgba(46,155,214,${0.08 + v * 0.85})` }}>
                    {ping && <span className="ring absolute inset-0 rounded-[3px] border-2 border-[#A6ECFF]" />}
                  </span>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="dim mt-3 flex items-center gap-2 text-[10.5px]">Menos<span className="h-2 w-28 rounded" style={{ background: 'linear-gradient(90deg, rgba(46,155,214,.1), rgba(46,155,214,.93))' }} />Más</div>
      </div>
    </div>
  );
}

/* 5 · Seguimiento automático */
const FOLLOW = [['Día 0', 'Propuesta enviada', 'Correo'], ['Día 3', 'Recordatorio con caso de éxito', 'WhatsApp'],
  ['Día 7', 'Invitación a demo de 20 minutos', 'Correo'], ['Día 15', 'Llamada del vendedor asignado', 'Tarea']];
export function CrmSeguimiento() {
  const t = useTick(1500);
  const step = t % 6;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.4fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col p-3">
        <Head title="Secuencia para Grupo Pacífico" sub="Se detiene sola cuando el cliente responde" right={step >= 5 ? <Status tone="ok">Respondió</Status> : <Live label="Activa" />} />
        <ol className="relative mt-3 flex flex-1 flex-col justify-around border-l border-white/15 pl-5">
          {FOLLOW.map(([d, s, ch], i) => {
            const sent = i < step;
            return (
              <li key={d} className="relative text-[12px]">
                <span className={`absolute -left-[27px] top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-[#0C2434] transition-colors duration-300 ${sent ? 'bg-[#4FBF6A]' : 'bg-white/20'}`} />
                <div className="flex flex-wrap items-center gap-2"><b className="num">{d}</b><span>{s}</span></div>
                <div className="dim text-[11px]">{ch}{sent ? ', enviado' : ', programado'}</div>
              </li>
            );
          })}
        </ol>
      </div>
      <div className="flex min-h-0 flex-col gap-2">
        <Kpi label="Valor del embudo" tone="ok"><Count value={486000 + t * 1200} prefix="$ " /></Kpi>
        <Kpi label="Tasa de conversión"><Count value={32} prefix="+" suffix=" %" /></Kpi>
        <Kpi label="Duración del ciclo comercial"><Count value={-50} suffix=" %" /></Kpi>
      </div>
    </div>
  );
}

export const crmScreens = [
  { t: 'Buyer persona', C: CrmPersona }, { t: 'Recorrido', C: CrmRecorrido }, { t: 'Segmentos', C: CrmSegmentos },
  { t: 'Interacciones', C: CrmCalor }, { t: 'Seguimiento', C: CrmSeguimiento },
];
